import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";

import HealthBadge from '../HealthBadge';
import Sparkline, { sumSeries } from '../Sparkline';

import AvailabilityReplica from '../../../types/cluster/AvailabilityReplica';
import AvailabilityDatabase from '../../../types/cluster/AvailabilityDatabase';

import { formatNumber } from '../../../helpers/utils';
import { TimeRangeContext } from '../../../context/TimeRangeContext';
import { buildRedirectQuery } from '../../../helpers/timeRangeUtils';

interface ReplicasTableProps {
    replicas: AvailabilityReplica[],
    databases: AvailabilityDatabase[]
}

type Variant = 'healthy' | 'warning' | 'critical' | 'neutral';

const healthVariant = (health: string): Variant => {
    if (health === 'HEALTHY') return 'healthy';
    if (health === 'PARTIALLY_HEALTHY') return 'warning';
    if (health === 'UNKNOWN') return 'neutral';
    return 'critical';
};

// A dash means the figure does not apply to this row -- SQL Server leaves the
// transport columns null on a primary's own rows, because it does not send to
// itself. That is different from RateCell's N/A, which means the rate could not
// be measured. Neither may render as zero: zero reads as "nothing is happening".
const NOT_APPLICABLE = '\u2013';

const formatSeconds = (seconds: number | null): string => {
    if (seconds === null || seconds === undefined) return NOT_APPLICABLE;
    if (seconds === 0) return '0s';
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return minutes > 0 ? `${minutes}m ${remainder}s` : `${remainder}s`;
};

const formatKb = (kb: number | null): string => {
    if (kb === null || kb === undefined) return NOT_APPLICABLE;
    return kb >= 1024 ? `${formatNumber(Math.round(kb / 1024))} MB` : `${formatNumber(kb)} KB`;
};

// Flow control is a per-replica figure rather than a per-database one, and it is
// null on the primary's own row, where SQL Server records none.
const formatFlowControl = (ms: number | null): string =>
    ms === null || ms === undefined ? NOT_APPLICABLE : `${ms.toFixed(1)} ms/s`;

function RateCell(props: { value: number | null, series: number[], variant: Variant, label: string, source?: string }) {
    const { value, series, variant, label, source } = props;

    // A rate is only shown when one could actually be derived. Rendering an
    // unknown rate as 0 would read as "nothing is happening", which is the
    // opposite of what it means.
    if (value === null || value === undefined) {
        return (
            <td className="text-end">
                <span className="text-muted"
                      data-tooltip-id="global-tooltip"
                      data-tooltip-content={`${label} is not available. Monitor this replica as a database instance to measure it.`}>N/A</span>
            </td>
        );
    }

    const measured = source === 'measured';
    return (
        <td className="text-end">
            <div className="rate-cell">
                <Sparkline data={series} variant={variant} tooltip={`${label} over the selected period`}/>
                <span className="rate-value"
                      data-tooltip-id="global-tooltip"
                      data-tooltip-content={measured
                          ? `${label}, measured from this replica`
                          : `${label}, as last reported by SQL Server`}>
                    {formatKb(value)}/s{measured ? '' : '*'}
                </span>
            </div>
        </td>
    );
}

// Combines a column across a replica's databases. Nulls are ignored and the
// result is null when nothing is known, so a replica whose databases all report
// nothing stays unknown rather than collapsing to a misleading zero.
//
// combine is invoked with exactly two arguments. reduce also passes the index
// and the source array, which a variadic combiner such as Math.max would fold
// into its result and turn into NaN.
export const rollup = (
    databases: AvailabilityDatabase[],
    pick: (database: AvailabilityDatabase) => number | null,
    combine: (a: number, b: number) => number,
): number | null => {
    const known = databases.map(pick)
        .filter((value): value is number => value !== null && value !== undefined);
    return known.length === 0 ? null : known.reduce((a, b) => combine(a, b));
};

function DatabaseRow(props: { database: AvailabilityDatabase, activityUrl: string | null }) {
    const { database, activityUrl } = props;
    const variant = healthVariant(database.synchronizationhealth);
    return (
        <tr className="ag-database-row">
            <td className="ps-5">
                <i className="fal fa-database fa-fw me-1" aria-hidden="true"/>
                {activityUrl
                    ? <Link to={activityUrl}>{database.name}</Link>
                    : database.name}
                {database.issuspended &&
                    <span className="badge bg-danger ms-2"
                          data-tooltip-id="global-tooltip"
                          data-tooltip-content="Data movement is suspended for this database">SUSPENDED</span>}
            </td>
            <td><HealthBadge value={database.synchronizationstate}/></td>
            <td/>
            <td className="text-end">{formatKb(database.logsendqueuekb)}</td>
            <RateCell value={database.logsendratekbsec} series={database.logsendrateseries}
                      variant={variant} label={`${database.name} log send rate`}
                      source={database.ratesource}/>
            <td className="text-end">{NOT_APPLICABLE}</td>
            <td className="text-end">{formatKb(database.redoqueuekb)}</td>
            <RateCell value={database.redoratekbsec} series={database.redorateseries}
                      variant={variant} label={`${database.name} redo rate`}
                      source={database.ratesource}/>
            <td className="text-end">{formatSeconds(database.estimateddatalossseconds)}</td>
            <td className="text-end">{formatSeconds(database.estimatedrecoverytimeseconds)}</td>
        </tr>
    );
}

function ReplicasTable(props: ReplicasTableProps) {
    const { replicas, databases } = props;
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set(replicas.map(replica => replica.id)));
    const timeRangeContext = useContext(TimeRangeContext);

    // Replicas that map to a monitored instance link through to its activity screen,
    // carrying the current time range so the two screens stay in step.
    const activityUrl = (instanceId: number | null): string | null =>
        instanceId === null
            ? null
            : `/instances/${instanceId}/activity?${buildRedirectQuery(timeRangeContext.timeRange)}`;

    const toggleRow = (replicaId: number) => {
        setExpandedRows(previous => {
            const next = new Set(previous);
            if (next.has(replicaId)) {
                next.delete(replicaId);
            } else {
                next.add(replicaId);
            }
            return next;
        });
    };

    return (
        <div className='table-responsive'>
            <table className="table">
                <thead>
                <tr>
                    <th>Replica / Database</th>
                    <th>Sync health</th>
                    <th>Failover</th>
                    <th className="text-end">Send queue</th>
                    <th className="text-end">Send rate</th>
                    <th className="text-end"
                        data-tooltip-id="global-tooltip"
                        data-tooltip-content="Time the primary spent throttled sending to this replica">Flow control</th>
                    <th className="text-end">Redo queue</th>
                    <th className="text-end">Redo rate</th>
                    <th className="text-end"
                        data-tooltip-id="global-tooltip"
                        data-tooltip-content="Estimated data loss if this replica took over now (RPO)">Est. data loss</th>
                    <th className="text-end"
                        data-tooltip-id="global-tooltip"
                        data-tooltip-content="Estimated time to recover on failover (RTO)">Est. recovery</th>
                </tr>
                </thead>
                <tbody>
                {replicas.map(replica => {
                    const replicaDatabases = databases.filter(database => database.replicaid === replica.id);
                    const expanded = expandedRows.has(replica.id);
                    const variant = healthVariant(replica.synchronizationhealth);
                    const replicaActivityUrl = activityUrl(replica.instanceid);

                    // Replica rows roll up their databases so the row stays
                    // meaningful when collapsed.
                    const total = (pick: (database: AvailabilityDatabase) => number | null) =>
                        rollup(replicaDatabases, pick, (a, b) => a + b);
                    const worst = (pick: (database: AvailabilityDatabase) => number | null) =>
                        rollup(replicaDatabases, pick, Math.max);

                    return (
                        <React.Fragment key={replica.id}>
                            <tr className="ag-replica-row" role="button" onClick={() => toggleRow(replica.id)}>
                                <td className="fw-bold text-nowrap">
                                    <i className={`fal fa-fw me-1 ${expanded ? 'fa-angle-down' : 'fa-angle-right'}`}
                                       aria-hidden="true"/>
                                    <i className={`fal fa-fw me-1 ${replica.role === 'PRIMARY' ? 'fa-crown' : 'fa-clone'}`}
                                       aria-hidden="true"/>
                                    {replicaActivityUrl
                                        ? <Link to={replicaActivityUrl}
                                                onClick={event => event.stopPropagation()}>{replica.servername}</Link>
                                        : replica.servername}
                                    <span className="text-muted fw-normal ms-1">
                                        {replica.role === 'PRIMARY'
                                            ? '(primary)'
                                            : replica.availabilitymode === 'SYNCHRONOUS_COMMIT' ? '(synchronous)' : '(asynchronous)'}
                                    </span>
                                </td>
                                <td>
                                    <HealthBadge value={replica.synchronizationhealth}/>
                                    {replica.connectedstate === 'DISCONNECTED' &&
                                        <span className="badge bg-danger ms-1">DISCONNECTED</span>}
                                </td>
                                <td>
                                    {replica.failovermode === 'AUTOMATIC' ? 'Automatic' : 'Manual'}
                                    {replica.role !== 'PRIMARY' && replica.failoverready !== null && (
                                        replica.failoverready
                                            ? <span className="text-muted ms-1">(no data loss)</span>
                                            : <i className="fal fa-exclamation-triangle fa-fw amber-icon ms-1"
                                                 data-tooltip-id="global-tooltip"
                                                 data-tooltip-content="Not ready for failover without data loss"
                                                 aria-hidden="true"/>
                                    )}
                                </td>
                                <td className="text-end fw-bold">{formatKb(total(database => database.logsendqueuekb))}</td>
                                <RateCell value={total(database => database.logsendratekbsec)}
                                          series={sumSeries(replicaDatabases.map(database => database.logsendrateseries))}
                                          variant={variant} label={`${replica.servername} log send rate`}
                                          source={replicaDatabases[0]?.ratesource}/>
                                <td className="text-end fw-bold">{formatFlowControl(replica.flowcontrolms)}</td>
                                <td className="text-end fw-bold">{formatKb(total(database => database.redoqueuekb))}</td>
                                <RateCell value={total(database => database.redoratekbsec)}
                                          series={sumSeries(replicaDatabases.map(database => database.redorateseries))}
                                          variant={variant} label={`${replica.servername} redo rate`}
                                          source={replicaDatabases[0]?.ratesource}/>
                                <td className="text-end fw-bold">{formatSeconds(worst(database => database.estimateddatalossseconds))}</td>
                                <td className="text-end fw-bold">{formatSeconds(worst(database => database.estimatedrecoverytimeseconds))}</td>
                            </tr>
                            {expanded && replicaDatabases.map(database =>
                                <DatabaseRow key={database.id} database={database}
                                             activityUrl={replicaActivityUrl}/>)}
                        </React.Fragment>
                    );
                })}
                </tbody>
            </table>
        </div>
    );
}

export default ReplicasTable;
